import { Link } from "react-router-dom";

import ArrowLeft from "../../components/LogoSVG/ArrowLeft";
import ArticleHeader2AuthorsMobile from "../../components/Global/ArticleHeader2AuthorsMobile";
import Header1Mobile from "../../components/Global/Header1Mobile";
import Paragraph from "../../components/Global/Paragraph";

import thumbnail from "../../public/mockup.png";
import thanoshanImage from "../../public/thanoshan.jpg";
import reactLogo from "../../public/react_logo.png";
import dockerLogo from "../../public/docker_logo.png";
import awsLogo from "../../public/aws_logo.png";
import fastApiLogo from "../../public/fastapi_logo.png";
import dataPreprocessing from "../../public/ENSF619_Datapreprocessing.png";
import architecture from "../../public/ENSF619-Architecture.png";
import backendPipeline from "../../public/ENSF619-Backend_Pipeline.png";
import softwareComponent from "../../public/ENSF619-SoftwareComponent.png";
import TechStackComponent from "../../components/Global/TechStackComponent";
import UI1 from "../../public/ensf619-UI1.png";
import UI2 from "../../public/ensf619-UI2.png";
import UI3 from "../../public/ensf619-UI3.png";
import Header2Mobile from "../../components/Global/Header2Mobile";

function ENSF619PageMobile() {
  return (
    <div className="mx-auto max-w-[730px] px-6 pb-24 pt-12">
      <Link to={"/"} className="flex items-center cursor-pointer">
        <ArrowLeft className="w-6 h-6" />
        <p className="ml-2 mt-1 font-normal text-gray-5 text-center hover:text-custom-white">
          Back
        </p>
      </Link>

      <ArticleHeader2AuthorsMobile
        thumbnail={thumbnail}
        title="GitHub Commit Vulnerability Prediction Using Machine Learning"
        description="Applying ML models to predict the likelihood that GitHub commits introduce vulnerabilities and to identify insecure code files, thereby enhancing security awareness of developers."
        date="Jan 03, 2026"
        secondAuthor="Thanoshan MV"
        pictureSecondAuthor={thanoshanImage}
        portfolioSecondAuthor="https://thanoshanmv.github.io"
      />

      <Header1Mobile>Acknowledging the Guidance</Header1Mobile>

      <Paragraph>
        We would like to express our appreciation to
        <span className=" mx-2">
          <a
            className=" text-custom-green"
            href="https://shaktilab.org"
            target="_blank"
          >
            Prof. Gouri Ginde
          </a>
        </span>
        for sharing her experience, providing thoughtful feedback, and
        supporting us throughout the project from start to finish.
      </Paragraph>
      <Header1Mobile>Overview</Header1Mobile>
      <Paragraph>
        This project serves as an empirical study on applying ML models to
        predict insecure code at the level of GitHub commit files by learning
        from historical commit data. We build an end-to-end machine learning
        pipeline and provide a simple interface that allows developers to
        receive security reports and prioritize their attention on high-risk
        files within a commit.
      </Paragraph>
      <Header1Mobile>Problem and Motivation</Header1Mobile>
      <Paragraph>
        Current static analysis tools have several
        <span className=" text-custom-green mx-1.5">limitations:</span>they
        rely on manually defined rules and struggle to detect new or unseen
        patterns, they often produce too many alerts, which can overwhelm
        developers, these tools require configuration knowledge and the results
        might be hard to understand
      </Paragraph>

      <Paragraph>
        Most importantly, they do not provide an overall risk assessment at the
        commit level. Our ML-based approach addresses these problems:
      </Paragraph>
      <Paragraph className="" isDot>
        It assigns a risk score to each commit so reviewers know where to focus
      </Paragraph>
      <Paragraph className="" isDot>
        Machine learning can capture patterns beyond fixed rules, reducing
        reliance on traditional static analysis
      </Paragraph>
      <Paragraph className="" isDot>
        It also helps reduce alert fatigue by highlighting only high-risk
        changes
      </Paragraph>
      <Paragraph className="" isDot>
        The tool is easy to use, developers simply paste a commit link and
        receive a clear security report
      </Paragraph>

      <Header1Mobile>High-level Layers</Header1Mobile>
      <img
        alt=""
        fetchPriority="high"
        decoding="async"
        data-nimg="1"
        className="-ml-6 bg-white w-[calc(100%+48px)] max-w-none mt-8"
        style={{ color: "transparent" }}
        src={architecture}
      ></img>

      <Header1Mobile>ML Component</Header1Mobile>

      <Header2Mobile>Dataset</Header2Mobile>
      <Paragraph>
        We used
        <span className=" mx-2">
          <a
            className=" text-custom-green"
            href="https://huggingface.co/datasets/hao-li/AIDev"
            target="_blank"
          >
            AIDev dataset
          </a>
        </span>
        from Mining Sofware Repository 2026 challenge, which is a large-scale
        dataset containing nearly 1 million pull requests created by five major
        AI agents from over 116,000 open-source repositories. The dataset
        provides detailed information on repositories, pull requests, commits,
        and review comments.
      </Paragraph>
      <Header2Mobile>Data Preprocessing</Header2Mobile>

      <img
        alt=""
        fetchPriority="high"
        decoding="async"
        data-nimg="1"
        className="-ml-6 bg-white w-[calc(100%+48px)] max-w-none mt-8 mb-8"
        style={{ color: "transparent" }}
        src={dataPreprocessing}
      ></img>

      <Paragraph>
        For our analysis, we collected commit-level data from the PR commit
        details subset, which originally contained 711,923 commits. Each record
        includes information such as the patch content, commit SHA, file name,
        file status, and the number of changes. Since our study focuses on the
        most common languages, we selected commits related to the top five file
        extensions:
        <span className=" mx-2 text-white">ts, py, tsx, go, and js.</span>
      </Paragraph>
      <Paragraph>
        Next, we cleaned the data by removing non-English characters from commit
        messages and excluding commits with missing patches. This reduced the
        dataset to 641,721 instances. After filtering for the selected file
        extensions, the final dataset contained
        <span className=" mx-2 text-white">270,384 commits.</span>We then
        scanned each patch using
        <span className=" mx-1.5">
          <a
            className=" text-custom-green"
            href="https://semgrep.dev"
            target="_blank"
          >
            Semgrep,
          </a>
        </span>
        a static analysis tool, and labeled each commit as vulnerable or
        non-vulnerable based on the scan results.
      </Paragraph>

      <Header2Mobile>Model Training</Header2Mobile>
      <Paragraph>
        We trained 4 ML models:
        <span className=" mx-1.5 text-white">
          Random Forest, XGBoost, Naive Bayes, and Logistic Regression
        </span>
        using two encoding methods: one-hot encoding and ordinal encoding. The
        features used are listed in the table below.
      </Paragraph>

      <div className="overflow-x-auto mb-8">
        <table className=" border border-gray-400 rounded-2xl shadow-sm">
          <thead className="">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold border-gray-400 border-b">
                Feature
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold border-gray-400 border-b">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="text-gray-6">
            <tr className="  text-pretty transition-colors">
              <td className="px-4 py-3 text-sm border-gray-400 border-b ">
                Total commit changes
              </td>
              <td className="px-4 py-3 text-sm border-gray-400 border-b">
                Total number of lines changed in a commit
              </td>
            </tr>
            <tr className="   transition-colors">
              <td className="px-4 py-3 text-sm border-gray-400  border-b ">
                Total commit additions
              </td>
              <td className="px-4 py-3 text-sm  border-gray-400  border-b">
                Total number of lines added in a commit
              </td>
            </tr>
            <tr className="   transition-colors">
              <td className="px-4 py-3 text-sm border-gray-400  border-b">
                Total commit deletions
              </td>
              <td className="px-4 py-3 text-sm border-gray-400  border-b">
                Total number of lines deleted in a commit
              </td>
            </tr>
            <tr className="   transition-colors">
              <td className="px-4 py-3 text-sm border-gray-400  border-b font-medium">
                Total file additions
              </td>
              <td className="px-4 py-3 text-sm border-gray-400  border-b">
                Total number of lines added in a file
              </td>
            </tr>
            <tr className="   transition-colors">
              <td className="px-4 py-3 text-sm border-gray-400  border-b font-medium">
                Total file deletions
              </td>
              <td className="px-4 py-3 text-sm border-gray-400  border-b">
                Total number of lines deleted in a file
              </td>
            </tr>
            <tr className="   transition-colors">
              <td className="px-4 py-3 text-sm border-gray-400  border-b font-medium">
                Total file changes
              </td>
              <td className="px-4 py-3 text-sm border-gray-400  border-b">
                Total number of lines changed in a file
              </td>
            </tr>
            <tr className="   transition-colors">
              <td className="px-4 py-3 text-sm border-gray-400  border-b font-medium">
                File extension
              </td>
              <td className="px-4 py-3 text-sm border-gray-400  border-b">
                File type based on its extension (e.g., .py, .ts, .go)
              </td>
            </tr>
            <tr className="   transition-colors">
              <td className="px-4 py-3 text-sm border-gray-400  border-b font-medium">
                File status
              </td>
              <td className="px-4 py-3 text-sm border-gray-400  border-b">
                Change status of the file (modified, added, removed, renamed)
              </td>
            </tr>
            <tr className="   transition-colors">
              <td className="px-4 py-3 text-sm border-gray-400  border-b font-medium">
                Vulnerability label
              </td>
              <td className="px-4 py-3 text-sm border-gray-400  border-b">
                Binary indicator of whether the file is vulnerable
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Paragraph>
        Model performance is evaluated using 4 metrics: Precision, Recall,
        Accuracy, and F1. Among these metrics, the
        <span className=" mx-1.5 text-custom-green">F1-score</span>is chosen as
        the main metric because it balances precision and recall and is suitable
        for imbalanced vulnerability data.
      </Paragraph>

      <Paragraph>
        Based on the results,
        <span className=" mx-1.5 text-white">Random Forest</span>and
        <span className=" mx-1.5 text-white">XGBoost</span>performed the best,
        each achieving the highest F1-score of 0.73. Because of this strong
        balance between precision and recall, we selected these two models for
        use in our tool.
      </Paragraph>

      <Header1Mobile>Software Component</Header1Mobile>

      <img
        alt=""
        fetchPriority="high"
        decoding="async"
        data-nimg="1"
        className="-ml-6 bg-white w-[calc(100%+52px)] max-w-none mt-8 mb-8"
        style={{ color: "transparent" }}
        src={softwareComponent}
      ></img>

      <Header2Mobile>Tech Stack</Header2Mobile>

      <Paragraph>
        To build the tool, we used FastAPI with Python for the backend server
        and React for the frontend interface. We deployed the backend as Docker
        containers on Amazon Elastic Container Service
        <span className=" mx-1.5">
          <a
            className=" text-custom-green"
            href="https://aws.amazon.com/ecs/"
            target="_blank"
          >
            (ECS).
          </a>
        </span>
        and hosted the frontend on Vercel. The ML models are integrated into the
        backend application as{" "}
        <code className="px-1 py-0.5 rounded bg-gray-300 text-gray-800 text-sm font-mono">
          .pkl
        </code>{" "}
        files, which are loaded when the server starts.
      </Paragraph>

      <div className="flex flex-row flex-wrap justify-evenly mt-12 mx-2">
        <TechStackComponent name="FastAPI" logo={fastApiLogo} />
        <TechStackComponent name="Docker" logo={dockerLogo} />
      </div>
      <div className="flex flex-row flex-wrap justify-evenly mb-12 mt-8 mx-2">
        <TechStackComponent name="AWS" logo={awsLogo} />
        <TechStackComponent name="ReactJs" logo={reactLogo} />
      </div>

      <Header2Mobile>CI/CD Pipeline Design</Header2Mobile>
      <Paragraph>
        For the backend, we use GitHub Actions with separated CI and CD
        pipelines.
      </Paragraph>

      <Paragraph className="" isDot>
        The CI pipeline is triggered on pull requests and when changes are
        merged into the main branch.
      </Paragraph>

      <Paragraph className="" isDot>
        During CI, we build a Docker image and push it to Amazon Elastic
        Container Registry
        <span className=" mx-1.5">
          <a
            className=" text-custom-green"
            href="https://aws.amazon.com/ecr/"
            target="_blank"
          >
            (ECR).
          </a>
        </span>
      </Paragraph>

      <Paragraph className="" isDot>
        The CD pipeline is responsible for deployment. When deploying, we
        manually select a specific Docker image tag and deploy it to Amazon
        Elastic Container Service
        <span className=" mx-1.5">
          <a
            className=" text-custom-green"
            href="https://aws.amazon.com/ecs/"
            target="_blank"
          >
            (ECS).
          </a>
        </span>
      </Paragraph>

      <Paragraph>
        We intentionally avoid fully automated CI/CD (where deployment happens
        immediately after a build). By manually choosing the build version, we
        gain better control over the deployment process and can easily roll back
        to a previous version if an issue occurs. This approach helps reduce
        risk and improves reliability in production deployments.
      </Paragraph>

      <img
        alt=""
        fetchPriority="high"
        decoding="async"
        data-nimg="1"
        className="-ml-6 bg-white w-[calc(100%+52px)] max-w-none mt-8 mb-8"
        style={{ color: "transparent" }}
        src={backendPipeline}
      ></img>

      <Paragraph>
        For the frontend, the process is simpler. We use Vercel for deployment
        and also maintain separate CI and CD workflows. This allows us to choose
        which build artifact to release, ensuring consistency and control over
        frontend releases as well.
      </Paragraph>

      <Header1Mobile>GitHub Commit Vulnerability Prediction</Header1Mobile>

      <div className="mt-8 mb-8 w-full space-y-6">
        {/* Top row: 2 images */}
        <div className="flex flex-col md:flex-row gap-6 w-full justify-center">
          <img
            alt=""
            className="bg-white w-full md:w-5/12 max-w-md mx-auto
                 border border-gray-100 rounded-lg shadow-sm"
            src={UI1}
          />
          <img
            alt=""
            className="bg-white w-full md:w-5/12 max-w-md mx-auto
                 border border-gray-100 rounded-lg shadow-sm"
            src={UI2}
          />
        </div>
        {/* Bottom row: 1 large image */}
        <div className="w-full flex justify-center">
          <img
            alt=""
            className="bg-white w-[calc(100%+30px)]  max-w-2xl
                 border border-gray-100 rounded-lg shadow-sm"
            src={UI3}
          />
        </div>
      </div>

      <Header1Mobile>Thank you for reading this post !!</Header1Mobile>
    </div>
  );
}

export default ENSF619PageMobile;
